/**
 * ImageAttribution — render the legally-required credit text for a
 * CC-licensed external photo.
 *
 * Renders nothing when:
 *   - asset is null/undefined
 *   - asset.source is empty, missing, or 'uploaded' (facility's own
 *     uploads need no credit)
 *   - source_attribution AND source_license are both empty (legacy /
 *     incomplete data — render nothing rather than a misleading bare
 *     "Image:" caption)
 *
 * Otherwise renders a small caption with the attribution text. CC
 * licenses require the credit to be visible "next to" the image
 * (Creative Commons "BY" clause). Keeping this component thin so it
 * can drop into product cards, hero areas, gallery thumbs without
 * forcing every parent to know the rules.
 *
 * (P-08-Images-Phase-2 S-08i2b, 2026-05-30.)
 */
import React from 'react';
import PropTypes from 'prop-types';

const SOURCE_LABEL = {
  inaturalist: 'iNaturalist',
  smithsonian_open_access: 'Smithsonian Open Access',
  bhl: 'Biodiversity Heritage Library',
  usgs: 'USGS ScienceBase',
};

const LICENSE_LABEL = {
  'cc0': 'CC0',
  'cc-by': 'CC BY',
  'cc-by-sa': 'CC BY-SA',
  'public-domain': 'Public Domain',
  'public_domain': 'Public Domain',
  'pd': 'Public Domain',
};

const normalizeLicense = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const s = raw.trim().toLowerCase().replace(/[\s_]+/g, '-').replace(/[-.]\d+(\.\d+)*$/g, '');
  return LICENSE_LABEL[s] || raw.trim();
};

const ImageAttribution = ({ asset, variant = 'caption', className = '' }) => {
  if (!asset) return null;
  const source = asset.source || '';
  if (!source || source === 'uploaded') return null;

  const attrText = (asset.source_attribution || '').trim();
  const licenseRaw = asset.source_license || '';
  const sourceLabel = SOURCE_LABEL[source] || source;
  const licenseLabel = normalizeLicense(licenseRaw);

  if (!attrText && !licenseLabel) return null;

  // Compact pill variant (used over thumbs in galleries / cards where
  // the full attribution would dominate). Hover shows full text.
  if (variant === 'pill') {
    return (
      <span
        className={`ff-image-attribution ff-image-attribution--pill ${className}`.trim()}
        title={attrText || `${sourceLabel}${licenseLabel ? ' · ' + licenseLabel : ''}`}
        style={{
          display: 'inline-block',
          fontSize: '0.65rem',
          lineHeight: '1.2',
          padding: '2px 6px',
          borderRadius: 8,
          background: 'rgba(0, 0, 0, 0.55)',
          color: '#fff',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        © {sourceLabel}
        {licenseLabel ? ` · ${licenseLabel}` : ''}
      </span>
    );
  }

  // Default: <figcaption>-style block under the image.
  return (
    <small
      className={`ff-image-attribution ff-image-attribution--caption ${className}`.trim()}
      style={{
        display: 'block',
        fontSize: '0.7rem',
        lineHeight: '1.3',
        color: 'var(--ff-text-secondary, #6b7280)',
        marginTop: 4,
      }}
    >
      {attrText
        ? attrText
        : `Source: ${sourceLabel}${licenseLabel ? ` (${licenseLabel})` : ''}`}
    </small>
  );
};

ImageAttribution.propTypes = {
  asset: PropTypes.shape({
    source: PropTypes.string,
    source_license: PropTypes.string,
    source_attribution: PropTypes.string,
  }),
  variant: PropTypes.oneOf(['caption', 'pill']),
  className: PropTypes.string,
};

export default ImageAttribution;
